// FeSummaryPage.jsx
import React from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
} from "react-bootstrap";
import api from "../../../Services/Api/api";
import FeDetailModal from "./FeDetailModal";
import { INR, fmtDate } from "../../../Modules/utils/format";
import NavBar from "../../Components/NavBar";
import { getFullName } from "../../../Modules/utils/fullname";
import {
  formatDateRange,
  formatPhone,
  getEndOfDay,
  toDateInputValue,
} from "../../../Modules/utils/etc";
import DateRangeFilter from "../../Components/DateRangeFilter";

/**
 * Props:
 *  - data: { overall: {...}, office: {...} }
 *
 * Expected (optional) global/helpers:
 *  - INR(number) -> formatted currency string
 *  - fmtDate(date) -> formatted date string
 *  - getFullName(office) -> string
 *
 * Notes:
 *  - Mobile-first responsive layout using react-bootstrap Grid.
 *  - Top row: 3 cards with 4 items each.
 *  - Bottom row: 3 cards with remaining items.
 *  - Component does not compute derived balances; it displays API fields.
 */

function TopSummary({ data = {} }) {
  const { overall = {}, officeExp = {} } = data;

  // small render helpers
  const format = (v) => (typeof INR === "function" ? INR(v) : String(v));
  const renderRowItem = (label, value = 0, opts = {}) => {
    const valueStyle = {
      fontFamily: "monospace",
      fontWeight: 600,
      color: opts.color || "inherit",
    };
    return (
      <div
        className="tsb-item"
        key={label}
        title={opts.title || label}
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "0px 0",
        }}
      >
        <div style={{ fontSize: 11, color: "#6c757d" }}>{label}</div>
        <div style={{ fontSize: 12, ...valueStyle }}>
          {(opts?.format || format)(value)}
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="tsb-root" style={{ padding: 12 }}>
      {/* Top Row: 3 cards (each with 4 items) */}
      <Row className="mb-1" xs={1} md={3}>
        <Col className="mb-1">
          <Card>
            <Card.Body>
              <Card.Title style={{ fontSize: 14, fontWeight: 600 }}>
                Operational Summary
              </Card.Title>
              <div style={{ marginTop: 8 }}>
                {renderRowItem("Total COD", overall.totalCOD)}
                {renderRowItem("Total Pickups", overall.totalPickups, {
                  format: (x) => x?.toString(),
                })}
                {renderRowItem("Total Deliveries", overall.totalDeliveries, {
                  format: (x) => x?.toString(),
                })}
                {renderRowItem(
                  "Total Commission",
                  overall.totalCommissionEarned
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col className="mb-1">
          <Card>
            <Card.Body>
              <Card.Title style={{ fontSize: 14, fontWeight: 600 }}>
                Office Transactions
              </Card.Title>
              <div style={{ marginTop: 8 }}>
                {renderRowItem(
                  "Commission Adjusted",
                  overall.totalCommissionAdjusted,
                  {
                    title:
                      "Commission adjusted to FE from last settlement cycle",
                  }
                )}
                {renderRowItem("Paid to FE", overall.totalPaidToFE)}
                {renderRowItem(
                  "Office Expenses",
                  officeExp?.totalOfficeExpenses
                )}
                {renderRowItem(
                  "Owner Collection",
                  officeExp?.totalOwnerCollections
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col className="mb-1">
          <Card>
            <Card.Body>
              <Card.Title style={{ fontSize: 14, fontWeight: 600 }}>
                FE Dues
              </Card.Title>
              <div style={{ marginTop: 8 }}>
                {renderRowItem("COD to Deposit", overall.totalCOD, {
                  title: "COD collected by FE that needs to be deposited",
                })}
                {renderRowItem("Total RTO Loss", overall.totalRtoLoss)}
                {renderRowItem("Amount Paid by FE", overall.totalDepositsByFE)}
                {renderRowItem(
                  "Outstanding Deposits",
                  overall.totalCOD +
                    overall.totalRtoLoss -
                    overall.totalDepositsByFE
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Bottom Row: 3 cards */}
      <Row xs={1} md={3}>
        <Col className="mb-1">
          <Card>
            <Card.Body>
              <Card.Title style={{ fontSize: 14, fontWeight: 600 }}>
                FE Balance Due
              </Card.Title>
              <div style={{ marginTop: 8 }}>
                {renderRowItem(
                  "Opening Balance Due",
                  overall.openingUserBalanceDue
                )}

                {renderRowItem(
                  "Closing Balance Due",
                  overall.closingUserBalanceDue
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col className="mb-1">
          <Card>
            <Card.Body>
              <Card.Title style={{ fontSize: 14, fontWeight: 600 }}>
                FE Commission Balance
              </Card.Title>
              <div style={{ marginTop: 8 }}>
                {renderRowItem(
                  "Opening Comm Balance",
                  overall.openingCommissionBalance
                )}

                {renderRowItem(
                  "Closing Comm Balance",
                  overall.closingCommissionBalance
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col className="mb-1">
          <Card>
            <Card.Body>
              <Card.Title style={{ fontSize: 14, fontWeight: 600 }}>
                Office Balance
              </Card.Title>
              <div style={{ marginTop: 8 }}>
                {renderRowItem(
                  "Opening Office Balance",
                  overall.openingOfficeBalance?.totalAvailable
                )}
                {renderRowItem(
                  "Closing Office Balance",
                  overall.closingOfficeBalance?.totalAvailable
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

/**
 * Props:
 *  - startDate: Date
 *  - endDate: Date
 *  - onChange: ({ startDate, endDate }) => void
 */
function SalaryCycleNavigator({ startDate, endDate, onChange, getCycle }) {
  if (!startDate || !endDate) return null;

  const prevCycle = () => {
    const base = new Date(startDate);
    base.setDate(base.getDate() - 1);
    return getCycle(base);
  };

  const nextCycle = () => {
    const base = new Date(endDate);
    base.setDate(base.getDate() + 1);
    return getCycle(base);
  };

  const formatLabel = (start, end) => {
    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = start.getMonth() === end.getMonth();

    const s = start.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: sameMonth ? undefined : "short",
      year: sameYear ? undefined : "numeric",
    });

    const e = end.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: sameYear ? undefined : "numeric",
    });

    return `${s}–${e}`;
  };

  const prev = prevCycle();
  const next = nextCycle();

  return (
    <div className="d-flex align-items-center gap-2">
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => onChange({ startDate: prev.start, endDate: prev.end })}
      >
        ← {formatLabel(prev.start, prev.end)}
      </Button>

      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => onChange({ startDate: next.start, endDate: next.end })}
      >
        {formatLabel(next.start, next.end)} →
      </Button>
    </div>
  );
}

class FeSummaryPage extends React.Component {
  state = {
    loading: false,
    error: null,
    filters: {
      range: "salary_cycle",
      startDate: this.getSalaryCycle().start,
      endDate: this.getSalaryCycle().end,
    },
    data: {
      office: null,
      overall: {},
      list: [],
      range: [],
    },
    showDetail: false,
    selectedFe: null,
  };

  componentDidMount() {
    this.fetchSummary();
  }

  getSalaryCycle(date = new Date()) {
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();

    if (d <= 15) {
      return {
        start: new Date(y, m, 1),
        end: new Date(y, m, 15, 23, 59, 59, 999),
      };
    }

    return {
      start: new Date(y, m, 16),
      end: new Date(y, m + 1, 0, 23, 59, 59, 999),
    };
  }

  shiftSalaryCycle(direction) {
    const { startDate } = this.state.filters;
    const base = startDate ? new Date(startDate) : new Date();

    const shifted =
      base.getDate() <= 15
        ? new Date(
            base.getFullYear(),
            base.getMonth(),
            direction === "prev" ? -15 : 16
          )
        : new Date(
            base.getFullYear(),
            base.getMonth() + (direction === "prev" ? 0 : 1),
            1
          );

    return this.getSalaryCycle(shifted);
  }

  buildQuery() {
    const { filters } = this.state;

    return {
      startDate: filters.startDate,
      endDate: filters.endDate,
    };
  }

  fetchSummary = async () => {
    try {
      this.setState({ loading: true, error: null });
      const query = this.buildQuery();
      // Adjust endpoint as per your backend; here we call v1/summary
      const res = await api.get("v1/office/summary", query);
      // Expected response structure:
      // { office:{...}, overall:{...}, agents:[{...}, ...] }
      this.setState({
        data: {
          office: res.office,
          overall: res.summary?.overall || {},
          list: res.summary?.agents || [],
          officeExp: res.summary?.officeExp,
          range: res.summary?.range,
        },
        loading: false,
      });
    } catch (e) {
      this.setState({ loading: false, error: e.message || "Failed to load" });
    }
  };

  handleRowClick = (agent) => {
    this.setState({ selectedFe: agent, showDetail: true });
  };

  closeDetail = (reload = false) => {
    this.setState({ showDetail: false, selectedFe: null });
    if (reload) this.fetchSummary();
  };

  renderTable() {
    const list = this.state.data.list || [];

    if (!list.length && !this.state.loading) {
      return (
        <div className="text-center py-4 text-muted">
          No Transactions found.
        </div>
      );
    }

    return (
      <div>
        <h5 className="mb-3">Field Executive Summary</h5>

        <div style={{ overflowX: "auto" }}>
          <Row xs={1} md={2} lg={3} className="g-3">
            {list.map((a) => {
              return (
                <Col key={a._id || a.agentId}>
                  <Card
                    style={{ cursor: "pointer", minWidth: 300 }}
                    onClick={() => this.handleRowClick(a.agent)}
                  >
                    <Card.Body>
                      <div style={{ fontWeight: 600 }}>
                        {getFullName(a.agent)}{" "}
                        <span style={{ fontSize: "10px" }}>
                          ({formatPhone(a.agent)})
                        </span>
                      </div>
                      <div className="small text-muted mb-2">
                        {a.workingArea || ""}
                      </div>

                      <div className="d-flex justify-content-between">
                        <span>Total Deliveries</span>
                        <span>{a.totalDeliveries || 0}</span>
                      </div>

                      <div className="d-flex justify-content-between">
                        <span>Total Pickups</span>
                        <span>{a.totalPickups || 0}</span>
                      </div>

                      <div className="d-flex justify-content-between">
                        <span>COD Total</span>
                        <span style={{ fontFamily: "monospace" }}>
                          {INR(a.totalCOD)}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between">
                        <span>Deposits by FE</span>
                        <span style={{ fontFamily: "monospace" }}>
                          {INR(a.depositsByFe)}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between">
                        <span>RTO Loss</span>
                        <span>{INR(a.totalRtoLoss)}</span>
                      </div>

                      <div className="d-flex justify-content-between fw-bold">
                        <span>User Balance</span>
                        <span style={{ fontFamily: "monospace" }}>
                          {INR(a.lastUserBalanceDue)}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between">
                        <span>Commission (current)</span>
                        <span>{INR(a.commissionEarned)}</span>
                      </div>

                      <div className="d-flex justify-content-between">
                        <span>Commission (Settled)</span>
                        <span>{INR(a.commissionAdjusted)}</span>
                      </div>

                      <div className="d-flex justify-content-between">
                        <span>Comm Balance</span>
                        <span style={{ fontFamily: "monospace" }}>
                          {INR(a.lastCommissionBalance)}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between">
                        <span>Paid to FE</span>
                        <span>{INR(a.paidToFe)}</span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      </div>
    );
  }

  render() {
    const { loading, error, showDetail, selectedFe, filters } = this.state;

    return (
      <>
        <NavBar activeLink="/summary" />
        <Container fluid className="gainner p-3">
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div className="mb-3">
              <h5 className="mb-1">Office Summary</h5>
            </div>

            <Card className="text-nowrap pb-0 p-2 mb-2 ">
              <div
                style={{
                  fontSize: "11px",
                  color: "gray",
                  textAlign: "center",
                }}
              >
                Date Range Filter
              </div>
              <DateRangeFilter
                filterType={filters?.range}
                startDate={filters?.startDate}
                endDate={filters?.endDate}
                maxDateCount={400}
                onChange={({
                  filterType: range,
                  start: startDate,
                  end: endDate,
                }) => {
                  this.setState(
                    (s) => ({
                      filters: { ...s.filters, startDate, endDate, range },
                    }),
                    () => this.fetchSummary({})
                  );
                }}
              />
            </Card>

            {loading ? (
              <div className="text-muted text-center">Loading...</div>
            ) : error ? (
              <div className="text-danger">{error}</div>
            ) : (
              <>
                <div
                  className="text-muted text-start"
                  style={{ fontSize: "8px" }}
                >
                  Showing results{" "}
                  {formatDateRange(this.state.data?.range || [])}
                </div>
                <TopSummary data={this.state.data} />
                {this.renderTable()}
              </>
            )}
          </div>

          <FeDetailModal
            show={showDetail}
            agent={selectedFe}
            onClose={() => this.closeDetail(false)}
            onCloseAndReload={() => this.closeDetail(true)}
            initialRange={
              this.state.filters.range === "custom"
                ? {
                    range: "cusotm",
                    startDate: this.state.filters.startDate,
                    endDate: this.state.filters.endDate,
                  }
                : {
                    range: "since_settlement",
                    startDate: null,
                    endDate: null,
                  }
            }
          />
        </Container>
      </>
    );
  }
}

export default FeSummaryPage;
